/**
 * Theme for the `/preview` harness, chosen by the `?theme=` query parameter.
 *
 * Paper's `PaperThemeService` writes the chosen theme to localStorage, so a
 * preview that used it would change the reviewer's own theme for the real
 * app on the same origin. A preview session provides this in its place
 * (`shell/paper-shell.providers.ts`): it paints `data-paper-theme` on
 * `<html>` exactly as Paper does and never persists anything. The nav's
 * theme toggle still works inside a preview, in memory.
 */

import { computed, effect, Injectable, signal } from '@angular/core';
import { PaperThemeService, RICOH_PAPER_THEMES } from '@ricoh-paper/angular';

type ThemeName = Parameters<PaperThemeService['setTheme']>[0];
type FontName = Parameters<PaperThemeService['setFont']>[0];

/** Short names a reviewer can type; any Paper theme name also works. */
export const PREVIEW_THEME_ALIASES: Readonly<Record<string, ThemeName>> = {
  light: 'workplace-paper',
  graphite: 'graphite',
  // Paper labels `workplace-slate` "Black" (0.29+); `black` is "True Black".
  black: 'workplace-slate',
  'true-black': 'black',
};

const KNOWN = new Set<string>(RICOH_PAPER_THEMES.map((t) => t.name));
const DARK = new Set<string>(
  RICOH_PAPER_THEMES.filter((t) => t.mode === 'dark').map((t) => t.name),
);

/** `?theme=` value → a Paper theme name; unknown or empty → light. */
export function resolvePreviewTheme(value: string | null | undefined): ThemeName {
  const key = (value ?? '').trim().toLowerCase();
  if (key in PREVIEW_THEME_ALIASES) return PREVIEW_THEME_ALIASES[key];
  if (KNOWN.has(key)) return key as ThemeName;
  return 'workplace-paper';
}

@Injectable()
export class PreviewThemeService {
  // Start on ?theme= so the first frame is already the right paper.
  private readonly _theme = signal<ThemeName>(
    resolvePreviewTheme(
      typeof window === 'undefined'
        ? null
        : new URLSearchParams(window.location.search).get('theme'),
    ),
  );
  private readonly _font = signal<FontName>('inter');

  readonly theme = this._theme.asReadonly();
  readonly font = this._font.asReadonly();
  readonly mode = computed<'light' | 'dark'>(() => (DARK.has(this._theme()) ? 'dark' : 'light'));
  readonly isDark = computed(() => this.mode() === 'dark');
  readonly isLight = computed(() => this.mode() === 'light');

  constructor() {
    effect(() => {
      if (typeof document === 'undefined') return;
      document.documentElement.setAttribute('data-paper-theme', this._theme());
      document.documentElement.setAttribute('data-paper-font', this._font());
    });
  }

  setTheme(name: ThemeName): void {
    if (KNOWN.has(name)) this._theme.set(name);
  }

  setFont(name: FontName): void {
    this._font.set(name);
  }
}
